import React, { useRef, useState } from 'react';
import TextField from '../TextField/TextField';
import IconButton from '../IconButton/IconButton';
import CancelOutlined from '../../../icons/CancelOutlined';
import CheckboxOutlined from '../../../icons/CheckboxOutlined';
import { StyledActionsContainer } from './TextFieldWithActions.styles';
import { iron, scienceBlue } from '../../../abstracts/colours';

export interface TextFieldWithActionsProps {
  onChange: (item: string) => void;
  onCancelEditing: () => void;
  onSaveField: () => void;
  dataQaId: string;
  disabled?: boolean;
  value?: string;
  label?: string;
  placeholder?: string;
  errorMsg?: string;
  helperText?: string;
}

const TextFieldWithActions = ({
  onChange,
  onCancelEditing,
  onSaveField,
  dataQaId,
  disabled,
  value,
  label,
  placeholder,
  errorMsg,
  helperText,
}: TextFieldWithActionsProps): JSX.Element => {
  const [disableSave, setDisableSave] = useState<boolean>(true);
  const [areActionsVisible, setActionsVisible] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(document.createElement('input'));

  const onValueChange = (item: string) => {
    if (disableSave && item) {
      setDisableSave(false);
    }
    onChange(item);
  };

  const saveField = () => {
    onSaveField();
    setDisableSave(true);
    inputRef.current.blur();
  };

  const cancelEditing = () => {
    onCancelEditing();
    setDisableSave(true);
    inputRef.current.blur();
  };

  const onFocus = (): void => setActionsVisible(true);

  const onBlur = (): void => setActionsVisible(false);

  const AdditionalActions = () => {
    const isSaveDisabled = disabled || !value || disableSave;
    return (
      <StyledActionsContainer>
        <IconButton
          onClick={saveField}
          color="primary"
          aria-label="save field"
          qaId={`save-${dataQaId}`}
          disabled={isSaveDisabled}
        >
          <CheckboxOutlined fill={!isSaveDisabled ? scienceBlue : iron} />
        </IconButton>
        <IconButton
          onClick={cancelEditing}
          color="primary"
          aria-label="cancel editing"
          qaId={`cancel-${dataQaId}`}
          disabled={disabled}
        >
          <CancelOutlined fill={disabled ? iron : scienceBlue} />
        </IconButton>
      </StyledActionsContainer>
    );
  };

  return (
    <div data-qa-id={`textFieldWithAction-container-${dataQaId}`} onFocus={onFocus} onBlur={onBlur}>
      <TextField
        dataQaId={dataQaId}
        disabled={disabled}
        label={label}
        value={value}
        placeholder={placeholder}
        helperText={helperText}
        onChange={onValueChange}
        errorMsg={errorMsg}
        onEnter={saveField}
        onEsc={cancelEditing}
        inputElRef={inputRef}
      />
      {areActionsVisible && <AdditionalActions />}
    </div>
  );
};

export default TextFieldWithActions;
